import { ensureFreshSession } from "@/lib/auth-refresh";
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Send, Headphones, Shield, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";

interface NavigationAction {
  label: string;
  path: string;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  sources?: string[];
  toolsUsed?: string[];
  navigationActions?: NavigationAction[];
}

const TOOL_LABELS: Record<string, string> = {
  get_workspace_overview: "Configurações",
  get_agents_summary: "Agentes",
  get_crm_pipeline_summary: "CRM",
  get_lead_detail: "CRM",
  get_lead_conversation_summary: "Conversas",
  get_products_summary: "Produtos",
  get_integrations_status: "Integrações",
  run_workspace_diagnostic: "Diagnóstico",
  get_stale_leads: "CRM",
  get_active_conversations: "Conversas",
  get_appointments_summary: "Agenda",
  get_dashboard_summary: "Dashboard",
  get_funnel_conversion_metrics: "Conversão",
  analyze_lead_conversion: "Conversão",
  suggest_navigation: "Navegação",
};

const QUICK_ACTIONS = [
  { label: "Resumo do workspace", message: "Me dê um resumo geral do meu workspace" },
  { label: "Taxa de conversão", message: "Qual a taxa de conversão dos meus leads nos últimos 30 dias?" },
  { label: "Diagnóstico", message: "Rode um diagnóstico completo da minha configuração" },
  { label: "Status integrações", message: "Qual o status das minhas integrações?" },
  { label: "Lead não converte", message: "Por que o lead não está convertendo? (informe o nome do lead na próxima mensagem)" },
];

const INITIAL_MESSAGE: ChatMessage = {
  role: "assistant",
  content:
    "Olá! Sou o Assistente FabChat — seu console do workspace.\n\n**O que posso fazer:**\n- Resumir configurações, agentes, CRM, produtos e integrações\n- Calcular taxa de conversão do funil e explicar por que um lead não converte\n- Diagnosticar problemas de configuração\n- Abrir atalhos para telas da plataforma\n\n**O que NÃO posso fazer:**\n- Enviar mensagens, editar registros ou acessar outros workspaces",
  sources: [],
};

function getSessionKey(workspaceId: string) {
  return `fabchat-support-${workspaceId}`;
}

function loadSession(workspaceId: string): ChatMessage[] {
  try {
    const stored = localStorage.getItem(getSessionKey(workspaceId));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return [INITIAL_MESSAGE];
}

function saveSession(workspaceId: string, messages: ChatMessage[]) {
  try {
    localStorage.setItem(getSessionKey(workspaceId), JSON.stringify(messages.slice(-40)));
  } catch { /* ignore */ }
}

export function clearSupportSession(workspaceId?: string) {
  if (workspaceId) {
    localStorage.removeItem(getSessionKey(workspaceId));
  } else {
    // Legacy cleanup
    localStorage.removeItem("fabchat-support-session");
  }
}

interface SupportAIChatProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
}

export function SupportAIChat({ isOpen, onClose, workspaceId }: SupportAIChatProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadSession(workspaceId));
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeToolLabel, setActiveToolLabel] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Reload session when workspace changes
  useEffect(() => {
    setMessages(loadSession(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    saveSession(workspaceId, messages);
  }, [messages, workspaceId]);

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 72) + 'px';
  };

  const sendMessage = useCallback(async (text?: string) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    if (!text) setInput("");
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setIsLoading(true);
    setActiveToolLabel("Processando...");

    try {
      const makeRequest = async (retried = false): Promise<Response> => {
        const session = await ensureFreshSession(retried);

        if (!session?.access_token) {
          throw new Error("Sua sessão expirou. Faça login novamente.");
        }

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-ai`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              workspace_id: workspaceId,
              messages: apiMessages,
              ui_context: { current_path: location.pathname },
            }),
          }
        );

        // Retry once on 401
        if (res.status === 401 && !retried) {
          return makeRequest(true);
        }

        return res;
      };

      const apiMessages = updatedMessages.map(m => ({ role: m.role, content: m.content }));
      const response = await makeRequest();

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Erro ao consultar o assistente.");
      }

      const data = await response.json();
      setMessages(prev => [
        ...prev,
        {
          role: "assistant",
          content: data.content,
          sources: data.sources || [],
          toolsUsed: data.tools_used || [],
          navigationActions: data.navigation_actions || [],
        },
      ]);
    } catch (err: any) {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: err.message || "Não consegui processar sua pergunta. Tente novamente.", sources: [] },
      ]);
    } finally {
      setIsLoading(false);
      setActiveToolLabel(null);
    }
  }, [input, isLoading, messages, workspaceId, location.pathname]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClearHistory = () => {
    setMessages([INITIAL_MESSAGE]);
    clearSupportSession(workspaceId);
  };

  if (!isOpen) return null;

  const showQuickActions = messages.length <= 1;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border bg-card shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-primary/5 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Headphones className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Assistente FabChat</h3>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary/30 text-primary">
                <Shield className="h-2.5 w-2.5 mr-0.5" />
                Somente leitura
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 1 && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground hover:text-foreground" onClick={handleClearHistory}>
              Limpar
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef as any}>
        <div className="space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}>
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                )}
              >
                {msg.role === "assistant" ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>h2]:text-sm [&>h2]:font-semibold [&>h2]:mt-2 [&>h3]:text-sm [&>h3]:font-medium [&>h3]:mt-1.5 [&_code]:bg-foreground/10 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-foreground/10 [&_pre]:p-2 [&_pre]:rounded-lg [&_pre]:text-xs [&_a]:text-primary [&_a]:underline">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p>{msg.content}</p>
                )}
                {/* Sources */}
                {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                  <div className="mt-2 pt-1.5 border-t border-foreground/10 flex flex-wrap gap-1">
                    {msg.toolsUsed.map((t, si) => (
                      <span key={si} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                        {TOOL_LABELS[t] || t.replace("get_", "").replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
                {msg.navigationActions && msg.navigationActions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {msg.navigationActions.map((nav, ni) => (
                      <button
                        key={ni}
                        type="button"
                        onClick={() => {
                          navigate(nav.path);
                          onClose();
                        }}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {nav.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Quick Actions */}
          {showQuickActions && !isLoading && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {QUICK_ACTIONS.map((qa, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(qa.message)}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-primary/20 text-primary hover:bg-primary/10 transition-colors"
                >
                  {qa.label}
                </button>
              ))}
            </div>
          )}

          {/* Loading indicator with tool label */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 text-sm flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {activeToolLabel || "Verificando com segurança..."}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="px-3 pb-3 pt-1">
        <div className="flex items-end gap-2 bg-muted/50 rounded-xl px-3 py-2 border">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              autoResize(e.target);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Faça uma pergunta..."
            rows={1}
            className="flex-1 bg-transparent text-sm resize-none outline-none max-h-[4.5rem] overflow-y-auto text-foreground placeholder:text-muted-foreground"
            disabled={isLoading}
          />
          <Button
            size="icon"
            className="h-7 w-7 shrink-0 rounded-lg"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
